/**
 * Image cropping utility for profile photo upload.
 *
 * Takes an image source (File object URL) plus the crop area + rotation from
 * react-easy-crop and returns a new File containing just the visible cropped
 * region, ready to upload to Sharetribe.
 */

const createImage = url =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', err => reject(err));
    // NOTE: do NOT set crossOrigin for blob: URLs — it's not needed (same
    // origin) and in some browsers it causes the image to fail to load or
    // the resulting canvas to be tainted, which would make canvas.toBlob()
    // return null.
    image.src = url;
  });

const degreesToRadians = degrees => (degrees * Math.PI) / 180;

/**
 * Compute the bounding box of a rectangle after rotation. Used to size a
 * canvas that can hold the rotated image without clipping it.
 */
const rotateSize = (width, height, rotation) => {
  const rotRad = degreesToRadians(rotation);
  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
};

/**
 * Crop the source image to the given pixel area and return a File.
 *
 * @param {string} imageSrc   Object URL (or data URL) of the source image
 * @param {object} pixelCrop  { x, y, width, height } from onCropComplete
 *                            (already in rotated-image coordinate space)
 * @param {number} rotation   Degrees the user rotated (0-360)
 * @param {string} fileName   Filename to use for the resulting File
 * @param {string} mimeType   MIME type of the output
 * @returns {Promise<File>}
 */
export default async function getCroppedImgFile(
  imageSrc,
  pixelCrop,
  rotation = 0,
  fileName = 'profile.jpg',
  mimeType = 'image/jpeg'
) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  const rotRad = degreesToRadians(rotation);

  // Size a first canvas big enough to hold the rotated image.
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation
  );
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // Translate to the centre of the bounding box so we can rotate about the
  // image centre, then draw the image.
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.translate(-image.width / 2, -image.height / 2);
  ctx.drawImage(image, 0, 0);

  // Now extract the cropped region from the rotated canvas and put it into
  // a second canvas of exactly the crop dimensions.
  const data = ctx.getImageData(pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height);

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  ctx.putImageData(data, 0, 0);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        const file = new File([blob], fileName, {
          type: mimeType,
          lastModified: Date.now(),
        });
        resolve(file);
      },
      mimeType,
      0.92
    );
  });
}
